import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, SelectDropdown, Label } from '@gsa/afp-component-library';
import { Controller, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import UpiidNumber from './upiid-number';
import SolicitationDetails from './solicitation-details';
import BidSelection from './bid-selection';
import {
  SOLICITATION_PROCESS,
  SOLICITATION_STATUS,
  TO_CONTINUE_FIELD,
} from '../../constants';
import { useUpiidDetails } from './upiid-details-provider';
import {
  getOptionsByUniqueKey,
  getCurrentAAC,
} from '../../utils/solicitation-helpers';
import './solicitation-upiid-details.scss';

/**
 * Reusable UPIID Details Form Component
 * Used across create, edit, and renew solicitation flows
 *
 * @param {Object} props
 * @param {Function} props.onSubmit - Form submission handler
 * @param {string} props.formId - Unique form ID (also used as data-testid)
 * @param {Object} props.solicitation - Solicitation object (for edit/renew flows, null for create)
 * @param {boolean} props.isRenewal - Whether this is a renewal flow
 */
const UpiidDetailsForm = ({
  onSubmit,
  formId = 'upiid-details-form',
  solicitation = null,
  isRenewal = false,
}) => {
  const navigate = useNavigate();
  const { handleSubmit, control, setValue } = useFormContext();
  const { options, setBidType } = useUpiidDetails();

  const isEditRenewal = useMemo(() => {
    return (
      !isRenewal &&
      solicitation?.solicitationType === SOLICITATION_PROCESS.RENEWAL
    );
  }, [solicitation, isRenewal]);

  // Determine solicitation process based on purchase type and renewal status
  const [solicitationProcess, setSolicitationProcess] = useState(() => {
    if (isRenewal || isEditRenewal) return SOLICITATION_PROCESS.RENEWAL;
    if (solicitation?.solicitationType) return solicitation.solicitationType;
    return SOLICITATION_PROCESS.RECOMPETE;
  });

  // Determine disabled states based on solicitation data
  const isSolicitationNumberDisabled = useMemo(() => {
    if (!solicitation) return false;
    const { solicitationType: type, solicitationStatus: status } = solicitation;
    return (
      type === SOLICITATION_PROCESS.RENEWAL ||
      [SOLICITATION_STATUS.OPEN, SOLICITATION_STATUS.CLOSED].includes(status) ||
      (type === SOLICITATION_PROCESS.STANDALONE &&
        status === SOLICITATION_STATUS.NEW)
    );
  }, [solicitation]);

  const isPurchaseTypeDisabled = useMemo(() => {
    if (isRenewal || isEditRenewal) return true;
    if (!solicitation) return false;
    return solicitation.solicitationStatus !== SOLICITATION_STATUS.DRAFT;
  }, [solicitation, isRenewal, isEditRenewal]);

  const isContractYearDisabled = useMemo(() => {
    if (!solicitation || isRenewal) return false;
    const { solicitationType: type, solicitationStatus: status } = solicitation;
    return (
      [SOLICITATION_STATUS.OPEN, SOLICITATION_STATUS.CLOSED].includes(status) ||
      (type === SOLICITATION_PROCESS.STANDALONE &&
        status === SOLICITATION_STATUS.NEW)
    );
  }, [solicitation, isRenewal]);

  // Handle purchase type change
  const handlePurchaseTypeChange = (purchaseTypeValue) => {
    if (['E', 'N', 'M'].includes(purchaseTypeValue)) {
      setSolicitationProcess(SOLICITATION_PROCESS.STANDALONE);
      setValue('bidType', 'OUTSIDE_FLEET');
      setBidType('OUTSIDE_FLEET');
    } else {
      setSolicitationProcess(
        isRenewal || isEditRenewal
          ? SOLICITATION_PROCESS.RENEWAL
          : SOLICITATION_PROCESS.RECOMPETE,
      );
      setValue('bidType', 'IN_FLEET');
      setBidType('IN_FLEET');
    }
  };

  // Initialize solicitation process on mount or when solicitation changes
  useEffect(() => {
    if (solicitation?.purchaseType) {
      handlePurchaseTypeChange(solicitation.purchaseType);
    }
    if (solicitation?.bidType) {
      setValue('bidType', solicitation.bidType);
    }
  }, [solicitation, setValue]);

  // Set hidden field for solicitationProcess
  useEffect(() => {
    setValue('solicitationProcess', solicitationProcess);
  }, [solicitationProcess, setValue]);

  // Get AAC options for UpiidNumber component
  const aacOptions = getOptionsByUniqueKey(options, 'aac');

  // Get default AAC for create flow
  const defaultUpiidAAC = solicitation ? undefined : getCurrentAAC(options);

  // Get contract year options based on flow type
  const contractYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = options
      .filter((item) => item.uniqueKey === 'year')
      .map((yearOption) => ({
        label: yearOption.label,
        value: yearOption.value,
      }))
      .sort((a, b) => a.value - b.value)
      .filter(
        (year) =>
          year.value >= currentYear || // only current and future years for all flows
          Number(year.value) === solicitation?.contractYear, // preserve existing contract year, if in the past, for edit flow
      );

    // For creating a new renewal - must be greater than than existing contract year it is renewing from
    if (isRenewal) {
      return years.filter((year) => year.value > solicitation.contractYear);
    }

    // For editing renewal solicitation - allow one year prior
    if (isEditRenewal) {
      return years.filter(
        (year) => year.value >= solicitation.contractYear - 1,
      );
    }

    // For create flow (no solicitation) or edit flow (not renewal) - allow current and future years
    return years;
  }, [options, solicitation, isRenewal, isEditRenewal]);

  // Get purchase type options based on flow type
  const purchaseTypeOptions = useMemo(() => {
    const purchaseTypes = getOptionsByUniqueKey(options, 'purchaseType');

    // For renewal flows: only S or Y allowed
    if (
      isRenewal ||
      solicitation?.solicitationType === SOLICITATION_PROCESS.RENEWAL
    ) {
      return purchaseTypes.filter((pt) => pt.value === 'S' || pt.value === 'Y');
    }

    return purchaseTypes;
  }, [options, isRenewal, solicitation]);

  // For renewal flows, ensure contract year is set to first available option
  useEffect(() => {
    if (isRenewal && contractYearOptions.length > 0) {
      const firstYearOption = contractYearOptions[0].value;
      setValue('contractYear', firstYearOption);
    }
  }, [isRenewal, contractYearOptions, setValue]);

  return (
    <form
      id={formId}
      onSubmit={handleSubmit(onSubmit)}
      className="solicitation-upiid-details-form margin-bottom-8"
      data-testid={formId}
      noValidate
    >
      <div className="grid-row grid-col-6 text-ink">
        Enter a new solicitation number to create your solicitation. If you are
        entering an existing solicitation number, then you will be prompted to
        proceed with renewing the solicitation.
      </div>

      <UpiidNumber
        control={control}
        aacOptions={aacOptions}
        isAacDisabled={isSolicitationNumberDisabled}
        isSqDisabled={isSolicitationNumberDisabled}
        defaultUpiidAAC={defaultUpiidAAC}
      />

      <div className="grid-row grid-col-6">
        <div className="grid-row grid-gap-2 detail-row">
          {/* Contract year */}
          <Controller
            data-testid="new-solicitation-contract-year"
            name="contractYear"
            control={control}
            render={({ field: { onChange, value, ref } }) => (
              <SelectDropdown
                data-testid="solicitation-contract-year-select"
                aria-label="contract year"
                aria-required="true"
                aria-disabled={isContractYearDisabled}
                onChange={(ev) => {
                  onChange(ev.target.value);
                }}
                label="Contract year"
                options={contractYearOptions}
                inputRef={ref}
                value={value}
                disabled={isContractYearDisabled}
                required
              />
            )}
          />

          {/* Purchase type */}
          <Controller
            data-testid="solicitation-purchase-type-select"
            name="purchaseType"
            control={control}
            render={({ field: { onChange, value, ref } }) => (
              <SelectDropdown
                data-testid="solicitation-purchase-type-select"
                aria-label="purchase type"
                aria-required="true"
                aria-disabled={isPurchaseTypeDisabled}
                onChange={(ev) => {
                  onChange(ev.target.value);
                  handlePurchaseTypeChange(ev.target.value);
                }}
                label="Purchase type"
                options={purchaseTypeOptions}
                inputRef={ref}
                value={value}
                disabled={isPurchaseTypeDisabled}
                required
              />
            )}
          />

          <div className="solicitation-process">
            <Label className="text-bold">Solicitation process </Label>
            <span>{solicitationProcess}</span>
          </div>
        </div>
      </div>

      {/* Hidden field for solicitationProcess */}
      <input
        type="hidden"
        name="solicitationProcess"
        value={solicitationProcess}
      />

      {solicitationProcess === SOLICITATION_PROCESS.STANDALONE && (
        <BidSelection />
      )}

      <SolicitationDetails />

      <div className="grid-row grid-gap-4 padding-left-2">
        <Button
          variant="unstyled"
          label="Cancel"
          onClick={() => {
            navigate('/catalog/solicitations');
          }}
        />
        <Button
          label="Save"
          type="submit"
          data-testid="save-btn"
          className="margin-right-2"
          onClick={() => {
            setValue(TO_CONTINUE_FIELD, false);
          }}
        />
        <Button
          label="Save and continue"
          type="submit"
          data-testid="save-continue-btn"
          rightIcon={{ name: 'arrow_forward' }}
          onClick={() => {
            setValue(TO_CONTINUE_FIELD, true);
          }}
        />
      </div>
    </form>
  );
};

UpiidDetailsForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  formId: PropTypes.string,
  solicitation: PropTypes.object,
  isRenewal: PropTypes.bool,
};

export default UpiidDetailsForm;
